//src/pages/inventory/Product/OCRResultDialog.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Alert,
  Chip,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  Warning,
  Delete,
  ErrorOutline,
} from "@mui/icons-material";
import dayjs from "dayjs";
import {
  DEFAULT_EXPIRY_THRESHOLD_MONTHS,
  isNearExpiryDate,
  isExpiredExpiryDate,
} from "../../../utils/medicineExpiryThreshold";
import { confirmAsync } from "../../../utils/appNotify";
import {
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../../utils/formModalStyles";

const OCRResultDialog = ({
  open,
  onClose,
  ocrData,
  onConfirm,
  expiryThresholdMonths = DEFAULT_EXPIRY_THRESHOLD_MONTHS,
}) => {
  const [editableData, setEditableData] = useState(ocrData);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingSupplier, setEditingSupplier] = useState(false);

  React.useEffect(() => {
    setEditableData(ocrData);
  }, [ocrData]);

  const isNearExpiry = (expiryDate) =>
    isNearExpiryDate(expiryDate, expiryThresholdMonths);

  const isExpired = (expiryDate) => isExpiredExpiryDate(expiryDate);

  const handleEdit = (index) => {
    setEditingIndex(index);
  };

  const handleSave = (index) => {
    setEditingIndex(null);
  };

  const handleCancel = (index) => {
    setEditableData(ocrData);
    setEditingIndex(null);
  };

  const handleFieldChange = (index, field, value) => {
    const updatedMedicines = [...editableData.medicines];
    updatedMedicines[index] = {
      ...updatedMedicines[index],
      [field]: value
    };
    setEditableData({
      ...editableData,
      medicines: updatedMedicines
    });
  };

  const handleSupplierFieldChange = (field, value) => {
    setEditableData({
      ...editableData,
      [field]: value
    });
  };

  const handleRemoveRow = (index) => {
    const updatedMedicines = editableData.medicines.filter((_, i) => i !== index);
    setEditableData({
      ...editableData,
      medicines: updatedMedicines
    });
    setEditingIndex(null);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError("");

    try {
      // Validate data
      const hasEmptyFields = editableData.medicines.some(medicine => 
        !medicine.name || !medicine.quantity || !medicine.costPerUnit
      );

      if (hasEmptyFields) {
        setError("Please fill in all required fields for all medicines");
        setLoading(false);
        return;
      }

      // Call the confirm callback
      const result = await onConfirm(editableData);
      
      // Show success message with details
      if (result && result.newMedicinesCreated && result.newMedicinesCreated.length > 0) {
        const newMedicineNames = result.newMedicinesCreated.map(m => m.medicine).join(', ');
        setError(`Success! ${result.message}. New medicines created: ${newMedicineNames}`);
        // Don't close dialog immediately to show the success message
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        // Close dialog immediately for existing medicines
        onClose();
      }
    } catch (error) {
      console.error("Error confirming OCR data:", error);
      setError(`Failed to add stock: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEditingIndex(null);
    setError("");
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={STANDARD_FORM_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>
        Review Extracted Invoice Data
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Please review and edit the extracted medicine information before adding to inventory. All data fields are editable.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> If any medicine doesn't exist in the system, it will be automatically created in the medicine database first, then added to inventory.
            </Typography>
          </Alert>
          
          {editableData.medicines?.some(m => isExpired(m.expiryDate) || isNearExpiry(m.expiryDate)) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> Some items are expired or near expiry. Review carefully and use the delete button to remove unwanted items before confirming.
              </Typography>
            </Alert>
          )}
          
          {/* Invoice Summary */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Invoice & Supplier Details
              </Typography>
              <Tooltip title={editingSupplier ? "Save Supplier Details" : "Edit Supplier Details"}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setEditingSupplier(!editingSupplier)}
                >
                  {editingSupplier ? <Save fontSize="small" /> : <Edit fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Invoice Number:</Typography>
                {editingSupplier ? (
                  <TextField
                    value={editableData.invoiceNumber || ''}
                    onChange={(e) => handleSupplierFieldChange('invoiceNumber', e.target.value)}
                    size="small"
                    sx={{ width: 150 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="bold">{editableData.invoiceNumber || '-'}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Invoice Date:</Typography>
                {editingSupplier ? (
                  <TextField
                    type="date"
                    value={editableData.invoiceDate || ''}
                    onChange={(e) => handleSupplierFieldChange('invoiceDate', e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 150 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="bold">
                    {editableData.invoiceDate ? dayjs(editableData.invoiceDate).format('D MMM YYYY') : '-'}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Supplier Name:</Typography>
                {editingSupplier ? (
                  <TextField
                    value={editableData.supplier || ''}
                    onChange={(e) => handleSupplierFieldChange('supplier', e.target.value)}
                    size="small"
                    sx={{ width: 200 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="bold">{editableData.supplier || '-'}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Supplier Contact:</Typography>
                {editingSupplier ? (
                  <TextField
                    value={editableData.supplierContact || ''}
                    onChange={(e) => handleSupplierFieldChange('supplierContact', e.target.value)}
                    size="small"
                    sx={{ width: 150 }}
                    placeholder="Phone/Email"
                  />
                ) : (
                  <Typography variant="body1" fontWeight="bold">{editableData.supplierContact || '-'}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Supplier Address:</Typography>
                {editingSupplier ? (
                  <TextField
                    value={editableData.supplierAddress || ''}
                    onChange={(e) => handleSupplierFieldChange('supplierAddress', e.target.value)}
                    size="small"
                    sx={{ width: 250 }}
                    placeholder="Full Address"
                  />
                ) : (
                  <Typography variant="body1" fontWeight="bold">{editableData.supplierAddress || '-'}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Total Amount:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ color: '#1976d2' }}>
                  ₹{editableData.totalAmount || '0.00'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Error/Success Display */}
          {error && (
            <Alert 
              severity={error.startsWith("Success!") ? "success" : "error"} 
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Medicines Table */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Extracted Medicine Items ({editableData.medicines?.length || 0})
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#ffebee', border: '1px solid #f44336', borderRadius: '50%' }} />
                <Typography variant="caption">Expired</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#fff9c4', border: '1px solid #ff9800', borderRadius: '50%' }} />
                <Typography variant="caption">Near Expiry</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: '#e8f5e9', border: '1px solid #4caf50', borderRadius: '50%' }} />
                <Typography variant="caption">Good</Typography>
              </Box>
            </Box>
          </Box>
          
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 360, width: "100%", mb: 2, overflow: "auto" }}
          >
            <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Medicine Name <span style={{ color: '#ff5252' }}>*</span>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Batch Number
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Quantity <span style={{ color: '#ff5252' }}>*</span>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Expiry Date
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Manufacturer
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Cost/Unit (₹) <span style={{ color: '#ff5252' }}>*</span>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' }}>
                    Total Cost (₹)
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold', minWidth: 120 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {editableData.medicines?.map((medicine, index) => {
                const expired = isExpired(medicine.expiryDate);
                const nearExpiry = !expired && isNearExpiry(medicine.expiryDate);
                const rowColor = expired ? '#ffebee' : nearExpiry ? '#fff9c4' : '#e8f5e9';
                
                return (
                <TableRow 
                  key={index} 
                  hover
                  sx={{ backgroundColor: medicine.expiryDate ? rowColor : 'inherit' }}
                >
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        value={medicine.name}
                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                        size="small"
                        fullWidth
                        required
                      />
                    ) : (
                      <Typography variant="body2">{medicine.name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        value={medicine.batchNumber}
                        onChange={(e) => handleFieldChange(index, 'batchNumber', e.target.value)}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body2">{medicine.batchNumber || '-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        type="number"
                        value={medicine.quantity}
                        onChange={(e) => handleFieldChange(index, 'quantity', parseInt(e.target.value) || 0)}
                        size="small"
                        fullWidth
                        required
                      />
                    ) : (
                      <Typography variant="body2">{medicine.quantity}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        type="date"
                        value={medicine.expiryDate}
                        onChange={(e) => handleFieldChange(index, 'expiryDate', e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {medicine.expiryDate ? dayjs(medicine.expiryDate).format('D MMM YYYY') : '-'}
                        </Typography>
                        {expired && (
                          <Tooltip title="This medicine is expired! Consider rejecting this item.">
                            <Chip 
                              label="EXPIRED" 
                              size="small" 
                              color="error" 
                              icon={<ErrorOutline />}
                              sx={{ height: 20 }}
                            />
                          </Tooltip>
                        )}
                        {nearExpiry && (
                          <Tooltip title={`This medicine expires in ${dayjs(medicine.expiryDate).diff(dayjs(), 'day')} days. Review before accepting.`}>
                            <Chip 
                              label="Near Expiry" 
                              size="small" 
                              color="warning" 
                              icon={<Warning />}
                              sx={{ height: 20 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        value={medicine.manufacturer}
                        onChange={(e) => handleFieldChange(index, 'manufacturer', e.target.value)}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Typography variant="body2">{medicine.manufacturer || '-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingIndex === index ? (
                      <TextField
                        type="number"
                        value={medicine.costPerUnit}
                        onChange={(e) => handleFieldChange(index, 'costPerUnit', parseFloat(e.target.value) || 0)}
                        size="small"
                        fullWidth
                        required
                      />
                    ) : (
                      <Typography variant="body2">₹{medicine.costPerUnit}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      ₹{(medicine.quantity * medicine.costPerUnit).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {editingIndex === index ? (
                        <>
                          <Tooltip title="Save Changes">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSave(index)}
                            >
                              <Save fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleCancel(index)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit Item">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(index)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={expired ? "Remove Expired Item" : nearExpiry ? "Remove Near-Expiry Item" : "Remove Item"}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={async () => {
                            if (expired || nearExpiry) {
                              handleRemoveRow(index);
                            } else {
                              const confirmed = await confirmAsync(
                                `Are you sure you want to remove "${medicine.name}"?`,
                                { title: "Remove item", tone: "warning", confirmLabel: "Remove" }
                              );
                              if (confirmed) {
                                handleRemoveRow(index);
                              }
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>

          {/* Summary */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Invoice Summary
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Items:</Typography>
                <Typography variant="h6" color="primary">{editableData.medicines?.length || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Quantity:</Typography>
                <Typography variant="h6" color="primary">
                  {editableData.medicines?.reduce((sum, med) => sum + (med.quantity || 0), 0) || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Total Cost:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  ₹{editableData.medicines?.reduce((sum, med) => sum + (med.quantity * med.costPerUnit || 0), 0).toFixed(2) || '0.00'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Expired Items:</Typography>
                <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                  {editableData.medicines?.filter(m => isExpired(m.expiryDate)).length || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Near Expiry:</Typography>
                <Typography variant="h6" sx={{ color: '#ff9800' }}>
                  {editableData.medicines?.filter(m => isNearExpiry(m.expiryDate)).length || 0}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              <span style={{ color: '#ff5252' }}>*</span> Required fields | 
              You can edit any field by clicking the edit icon | 
              Remove unwanted items using the delete icon
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button 
          onClick={handleClose} 
          color="primary" 
          variant="outlined"
          sx={FORM_CANCEL_BUTTON_SX}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          variant="contained"
          sx={FORM_SUBMIT_BUTTON_SX}
          disabled={loading || !editableData.medicines?.length}
        >
          {loading ? 'Adding to Inventory...' : 'Add to Inventory'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OCRResultDialog;
